import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { IconButton } from '@startuptools/ui/IconButton';
import { Link } from '@startuptools/ui/Link';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Option } from '@startuptools/ui/Option';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Select } from '@startuptools/ui/Select';
import { Stack } from '@startuptools/ui/Stack';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { Typography } from '@startuptools/ui/Typography';
import {
  GqlWarrantProgram,
  type GqlCurrency,
  type GqlWpSetWisePriceInput,
} from '../../../../../graphql/base-types.graphql';
import {
  useWiseDependencyBondsSuspenseQuery,
  useWpSetWisePriceMutation,
  WiseDependencyBondsDocument,
} from '../../../../../graphql/react-operations';
import { Trans, useLingui } from '@lingui/react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { currencies } from '../../../../../components/currencies';
import { useEffect } from 'react';
import { isEmpty, isNil } from 'lodash-es';
import { BigNumber } from 'bignumber.js';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useOnce } from '@startuptools/common/react-hooks';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { useWarrantProgramSuspenseQuery, WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';

type FormValues = {
  currency: GqlCurrency;
  numberOfShares: string;
  wiseFloor: string;
  wiseCeil: string;
  investedAmount: string;
  discount: string;
  pricePerWarrant: string;
  maxNumberOfOptions: string;
};

const floorMaxNumberOfOptions = ({
  maxNumberOfOptions,
  pricePerWarrant,
}: {
  maxNumberOfOptions: string | null | undefined;
  pricePerWarrant: string | null | undefined;
}): { maxNumberOfOptions: string | null; investedAmount: string | null } => {
  if (!isNil(maxNumberOfOptions) && !isNil(pricePerWarrant)) {
    const flooredMaxNumberOfOptions = new BigNumber(maxNumberOfOptions).integerValue(BigNumber.ROUND_FLOOR);
    return {
      maxNumberOfOptions: flooredMaxNumberOfOptions.toString(),
      investedAmount: flooredMaxNumberOfOptions.times(pricePerWarrant).toString(),
    };
  }
  return {
    maxNumberOfOptions: null,
    investedAmount: null,
  };
};

export const StepWisePriceNgWrap = ({ warrantProgramId }: { warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const {
    data: { warrantProgram },
  } = useWarrantProgramSuspenseQuery({ variables: { companyId, warrantProgramId }, fetchPolicy: 'cache-and-network' });
  return <StepWisePrice warrantProgram={warrantProgram} />;
};

export const StepWisePrice = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'currency'
    | 'wiseFloor'
    | 'wiseCeil'
    | 'illiquidityDiscount'
    | 'valuationNumberOfShares'
    | 'wiseFullyDiluted'
    | 'maxNumberOfOptions'
    | 'pricePerWarrant'
  >;
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();
  const {
    data: { wiseDependencyBonds },
  } = useWiseDependencyBondsSuspenseQuery({
    variables: { companyId, warrantProgramId: warrantProgram.id },
    fetchPolicy: 'cache-and-network',
  });

  const [doSubmit, { loading }] = useWpSetWisePriceMutation({
    refetchQueries: [WiseDependencyBondsDocument, WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const { control, watch, setValue, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      currency: warrantProgram.currency,
      numberOfShares: warrantProgram.valuationNumberOfShares ?? undefined,
      wiseFloor: warrantProgram.wiseFloor ?? undefined,
      wiseCeil: warrantProgram.wiseCeil ?? undefined,
      discount: warrantProgram.illiquidityDiscount
        ? new BigNumber(warrantProgram.illiquidityDiscount).times(100).toString()
        : '0',
      maxNumberOfOptions: warrantProgram.maxNumberOfOptions ?? undefined,
    },
  });

  useEffect(() => {
    if (!isNil(warrantProgram.valuationNumberOfShares)) {
      const bonds = warrantProgram.wiseFullyDiluted
        ? wiseDependencyBonds.reduce((sum, b) => sum + Number(b.numberOfOptions), 0)
        : 0;
      const numberOfShares = Number(warrantProgram.valuationNumberOfShares) + bonds;
      setValue('numberOfShares', numberOfShares.toString());
    }
  }, [warrantProgram.valuationNumberOfShares, warrantProgram.wiseFullyDiluted, wiseDependencyBonds, setValue]);

  useOnce(() => {
    if (!isNil(warrantProgram.maxNumberOfOptions) && !isNil(warrantProgram.pricePerWarrant)) {
      setValue(
        'investedAmount',
        new BigNumber(warrantProgram.maxNumberOfOptions).times(warrantProgram.pricePerWarrant).toString(),
      );
    }
  });

  const selectedCurrency = watch('currency');
  const numberOfShares = watch('numberOfShares');
  const wiseFloor = watch('wiseFloor');
  const wiseCeil = watch('wiseCeil');
  const investedAmount = watch('investedAmount');
  const pricePerWarrant = watch('pricePerWarrant');
  const maxNumberOfOptions = watch('maxNumberOfOptions');

  useEffect(() => {
    if (!isNil(numberOfShares) && !isNil(wiseCeil)) {
      setValue('pricePerWarrant', new BigNumber(wiseCeil).div(numberOfShares).toString());
    }
  }, [setValue, numberOfShares, wiseCeil]);

  useEffect(() => {
    if (!isNil(investedAmount) && !isNil(pricePerWarrant)) {
      setValue('maxNumberOfOptions', new BigNumber(investedAmount).div(pricePerWarrant).toString());
    }
  }, [setValue, investedAmount, pricePerWarrant]);

  const handleFloorMaxNumberOfOptions = () => {
    const floored = floorMaxNumberOfOptions({ pricePerWarrant, maxNumberOfOptions });
    if (!isNil(floored.maxNumberOfOptions) && !isNil(floored.investedAmount)) {
      setValue('maxNumberOfOptions', floored.maxNumberOfOptions);
      setValue('investedAmount', floored.investedAmount);
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async data => {
    const floored = floorMaxNumberOfOptions({
      maxNumberOfOptions: data.maxNumberOfOptions,
      pricePerWarrant: data.pricePerWarrant,
    });
    if (isNil(floored.maxNumberOfOptions)) {
      throw new Error('unexpected undefined: floored.maxNumberOfOptions');
    }
    handleFloorMaxNumberOfOptions();
    const input: GqlWpSetWisePriceInput = {
      id: warrantProgram.id,
      currency: data.currency,
      wiseCeil: data.wiseCeil,
      wiseFloor: data.wiseFloor,
      illiquidityDiscount: new BigNumber(data.discount).div(100).toString(),
      pricePerWarrant: data.pricePerWarrant,
      maxNumberOfOptions: floored.maxNumberOfOptions,
    };
    try {
      await doSubmit({ variables: { companyId, input } });
      return stepNext();
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
  };

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Trans id="Price of the Convertible" />
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="row" gap={4}>
          <Stack gap={2} sx={{ minWidth: 340, maxWidth: 340 }}>
            <Controller
              control={control}
              name="currency"
              render={({ field: { value, ...props } }) => (
                <FormControl required>
                  <FormLabel>
                    <Trans id="Currency" />
                  </FormLabel>
                  <Select value={value} onChange={(_e, value) => props.onChange(value)}>
                    {currencies.map(c => (
                      <Option key={c.code} value={c.code}>
                        {c.code} - {c.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="numberOfShares"
              render={({ field }) => (
                <FormControl>
                  <FormLabel>
                    <Trans id="Number of Shares today" />
                  </FormLabel>
                  <NumericFormatInput {...field} lang={i18n.locale} readOnly tabIndex={-1} />
                  <FormHelperText>
                    <Trans id="This is calculated in the prior step" />
                  </FormHelperText>
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="wiseFloor"
              render={({ field }) => (
                <FormControl required>
                  <FormLabel>
                    <Trans id="Floor" />
                  </FormLabel>
                  <NumericFormatInput {...field} lang={i18n.locale} endDecorator={selectedCurrency} />
                  <FormHelperText>
                    <Trans id="Company valuation at the lowest conversion price" />
                  </FormHelperText>
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="wiseCeil"
              rules={{ validate: { minValue: (value, form) => new BigNumber(value).gte(form.wiseFloor) } }}
              render={({ field, fieldState }) => (
                <FormControl required error={fieldState.invalid}>
                  <FormLabel>
                    <Trans id="Ceiling" />
                  </FormLabel>
                  <NumericFormatInput
                    {...field}
                    lang={i18n.locale}
                    endDecorator={selectedCurrency}
                    onBlur={handleFloorMaxNumberOfOptions}
                  />
                  {fieldState.error ? (
                    <>
                      {fieldState.error.type === 'minValue' && (
                        <FormHelperText>
                          <Trans
                            id="Must be larger than floor ({floor})"
                            values={{
                              floor: (
                                <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                                  {wiseFloor}
                                </NumberFormat>
                              ),
                            }}
                          />
                        </FormHelperText>
                      )}
                    </>
                  ) : (
                    <FormHelperText>
                      <Trans id="Company valuation at the highest conversion price" />
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="investedAmount"
              render={({ field }) => (
                <FormControl required>
                  <FormLabel>
                    <Trans id="Invested Amount" />
                  </FormLabel>
                  <NumericFormatInput
                    {...field}
                    lang={i18n.locale}
                    endDecorator={selectedCurrency}
                    required
                    onBlur={handleFloorMaxNumberOfOptions}
                  />
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="discount"
              rules={{ validate: { maxValue: value => (!isEmpty(value) ? new BigNumber(value).lte(100) : undefined) } }}
              render={({ field, fieldState }) => (
                <FormControl required error={fieldState.invalid}>
                  <FormLabel>
                    <Trans id="Discount" />
                  </FormLabel>
                  <NumericFormatInput {...field} lang={i18n.locale} endDecorator="%" required />
                  {fieldState.invalid && fieldState.error?.type === 'maxValue' && (
                    <FormHelperText>
                      <Trans id="Discount can't be more than 100%" />
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="pricePerWarrant"
              render={({ field }) => (
                <FormControl>
                  <FormLabel>
                    <Trans id="Price per WISE Convertible" />
                  </FormLabel>
                  <NumericFormatInput
                    {...field}
                    lang={i18n.locale}
                    endDecorator={selectedCurrency}
                    readOnly
                    tabIndex={-1}
                  />
                  <FormHelperText>
                    <Trans id="Filled automatically" />
                  </FormHelperText>
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="maxNumberOfOptions"
              render={({ field }) => (
                <FormControl>
                  <FormLabel>
                    <Trans id="Number of WISE Convertibles" />
                  </FormLabel>
                  <NumericFormatInput
                    {...field}
                    lang={i18n.locale}
                    endDecorator={
                      <Tooltip title={<Trans id="Round number of shares" />}>
                        <IconButton onClick={handleFloorMaxNumberOfOptions}>
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    }
                    readOnly
                    tabIndex={-1}
                  />
                  <FormHelperText>
                    <Trans id="Filled automatically" />
                  </FormHelperText>
                </FormControl>
              )}
            />
            {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}
            <Box>
              <Button type="submit" loading={loading}>
                <Trans id="Save and Continue" />
              </Button>
            </Box>
          </Stack>
          <Box>
            <Alert sx={{ maxWidth: 600 }}>
              <Stack gap={2}>
                <Typography component="p">
                  <Trans id="In a WISE-convertible a discount is normally applied to the investment in the next round. If you, for example, give 20% discount and the next share issue is at 10 kr per share, the WISE will convert at 8 kr per share." />
                </Typography>
                <Typography component="p">
                  <Trans id='If there is no new share issue during the maturity of the WISE, it will convert to shares at a certain lowest valuation which is called "floor". You can also enter a "cap", which will be the highest valuation at which a conversion can take place.' />
                </Typography>
                <Typography component="p">
                  <Trans id="If you in the example above has entered a cap of 15 kr per share, still has a discount of 20% and the next share issue is done at a price per share of 25 kr, the conversion price would normally be 20 kr (25 kr with 20% discount) without a cap. But since there is a cap the conversion is done at 15 kr per share instead." />
                </Typography>
                <Typography component="p">
                  <Trans
                    id="For more information about how to reason around discount, floor and cap. See our article about WISE <link>here</link>"
                    components={{ link: <Link href="https://bolago.com/wise-convertible" target="_blank" /> }}
                  />
                </Typography>
              </Stack>
            </Alert>
          </Box>
        </Stack>
      </form>
    </Stack>
  );
};
